import { authHeader, utils } from "../helpers/";

const apiUrl = process.env.REACT_APP_API_URL;

export interface ClassroomCreator {
    id: number;
    name: string;
    email: string;
    avatar: string | null;
}

export interface ClassroomLevel {
    id: number;
    name: string;
    pivot?: {
        classroom_id: number;
        level_id: number;
    };
}

export interface ClassroomSummary {
    id: number;
    name: string;
    created_by: number;
    users_count: number;
    creator: ClassroomCreator;
    levels: ClassroomLevel[];
}

export interface ClassroomUserPivot {
    classroom_id: number;
    user_id: number;
    created_at?: string;
    updated_at?: string;
}

export interface ClassroomUser {
    id: number;
    name: string;
    email: string;
    avatar: string | null;
    pivot?: ClassroomUserPivot;
}

export interface ClassroomDetail extends Omit<ClassroomSummary, "users_count"> {
    users: ClassroomUser[];
}

export interface ClassroomContentItem {
    id: number;
    title: string;
    is_active: boolean;
    level: { id: number | null; name: string | null };
    category: { id: number | null; name: string | null };
    topic: { id: number | null; name: string | null };
    content_type: { id: number | null; name: string | null };
    is_attached: boolean;
}

export interface PaginatedResponse<T> {
    data: T[];
    meta: {
        page: number;
        per_page: number;
        total: number;
        total_pages: number;
    };
}

export const classroomService = {
    index,
    store,
    show,
    update,
    destroy,
    attachStudents,
    detachStudent,
    contents,
    attachContents,
    detachContents,
};

function index(): Promise<ClassroomSummary[]> {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
    };

    return fetch(`${apiUrl}/api/classrooms`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as ClassroomSummary[];
        })
        .catch((error) => Promise.reject(error));
}

function store(payload: { name: string; level_ids?: number[] }): Promise<ClassroomDetail> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify(payload),
    };

    return fetch(`${apiUrl}/api/classrooms`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as ClassroomDetail;
        })
        .catch((error) => Promise.reject(error));
}

function show(classroomId: number): Promise<ClassroomDetail> {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as ClassroomDetail;
        })
        .catch((error) => Promise.reject(error));
}

function update(classroomId: number, payload: { name: string; level_ids?: number[] }): Promise<ClassroomDetail> {
    const requestOptions: RequestInit = {
        method: "PUT",
        headers: authHeader(),
        body: JSON.stringify(payload),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as ClassroomDetail;
        })
        .catch((error) => Promise.reject(error));
}

function destroy(classroomId: number): Promise<{ success?: boolean }> {
    const requestOptions: RequestInit = {
        method: "DELETE",
        headers: authHeader(),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as { success?: boolean };
        })
        .catch((error) => Promise.reject(error));
}

function attachStudents(classroomId: number, userIds: Array<string | number>): Promise<ClassroomDetail> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({ user_ids: userIds.map((u) => `${u}`) }),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}/attach-student`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as ClassroomDetail;
        })
        .catch((error) => Promise.reject(error));
}

function detachStudent(classroomId: number, userId: string | number): Promise<ClassroomDetail> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({ user_id: `${userId}` }),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}/detach-student`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as ClassroomDetail;
        })
        .catch((error) => Promise.reject(error));
}

function contents(
    classroomId: number,
    params: {
        page?: number;
        per_page?: number;
        search?: string;
        sort_by?: string;
        sort_dir?: "asc" | "desc";
        level_id?: number;
        category_id?: number;
        topic_id?: number;
        content_type_id?: number;
        is_active?: boolean;
        is_attached?: boolean;
    } = {}
): Promise<PaginatedResponse<ClassroomContentItem>> {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
    };

    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        if (typeof value === "boolean") {
            query.append(key, value ? "1" : "0");
        } else {
            query.append(key, String(value));
        }
    });
    const qs = query.toString();

    return fetch(`${apiUrl}/api/classrooms/${classroomId}/contents${qs ? `?${qs}` : ""}`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as PaginatedResponse<ClassroomContentItem>;
        })
        .catch((error) => Promise.reject(error));
}

function attachContents(classroomId: number, contentIds: number[]): Promise<{ success?: boolean; message?: string }> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({ content_ids: contentIds }),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}/contents/attach`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as { success?: boolean; message?: string };
        })
        .catch((error) => Promise.reject(error));
}

function detachContents(classroomId: number, contentIds: number[]): Promise<{ success?: boolean; message?: string }> {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        body: JSON.stringify({ content_ids: contentIds }),
    };

    return fetch(`${apiUrl}/api/classrooms/${classroomId}/contents/detach`, requestOptions)
        .then(utils.handleResponse)
        .then((data) => {
            return data as { success?: boolean; message?: string };
        })
        .catch((error) => Promise.reject(error));
}


